import { io } from "socket.io-client";
import { useEffect, useRef, useState } from "react";
import axios from "axios";

// Socket is module-level so it persists across renders
const socket = io("http://localhost:30032", { autoConnect: false });

function SupportChat({ user, initialTicket }) {
  const roomId = `support_${user.userId}`;
  const [ticket] = useState(initialTicket);
  const [stage, setStage] = useState(
    initialTicket?.status === "assigned" ? "chat" : "waiting"
  );
  const [messages, setMessages] = useState([]);
  const [messageInput, setMessageInput] = useState("");
  const [typing, setTyping] = useState(false);

  const [isRecording, setIsRecording] = useState(false);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const [audioPreviewUrl, setAudioPreviewUrl] = useState(null);
  const [recordingBlob, setRecordingBlob] = useState(null);

  const fileInputRef = useRef();
  const chatContainerRef = useRef();
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const timerRef = useRef(null);
  const typingTimerRef = useRef(null);
  const messageIdRef = useRef(0);
  const isTypingRef = useRef(false);

  // ── Socket setup ──────────────────────────────────────────
  useEffect(() => {
    function onConnect() {
      socket.emit("registerUser", { userId: user.userId, role: "user" });
      socket.emit("startSupportChat", { userId: user.userId });
      socket.emit("loadChatHistory", { roomId, ticketId: ticket?.ticketId });
    }

    // Use .on with named handlers so we can remove them specifically
    // instead of removeAllListeners() which nukes everything
    function onChatHistory(history) {
      const formatted = history.map(msg => ({
        ...msg,
        bubbleSide: msg.senderType === "customer" ? "user"
          : msg.senderType === "system" ? "system" : "agent",
        msgType: msg.type,
      }));
      setMessages(formatted);
    }

    function onReceiveMessage(data) {
      const incoming = {
        ...data,
        bubbleSide: data.senderType === "customer" ? "user"
          : data.senderType === "system" ? "system" : "agent",
        msgType: data.type,
      };
      setMessages(prev => {
        if (data.tempId) {
          // Replace optimistic bubble with confirmed message
          const exists = prev.some(m => m._id === data.tempId);
          if (exists) {
            return prev.map(m =>
              m._id === data.tempId ? { ...incoming, status: "sent" } : m
            );
          }
        }
        // Deduplicate by real _id to avoid doubles
        if (prev.some(m => m._id === data._id)) return prev;
        return [...prev, { ...incoming, status: "sent" }];
      });
    }

    function onAgentJoined() {
      setStage("chat");
      socket.emit("loadChatHistory", { roomId, ticketId: ticket?.ticketId });
    }

    function onTyping() { setTyping(true); }
    function onStopTyping() { setTyping(false); }

    // Attach named listeners
    socket.on("connect", onConnect);
    socket.on("chatHistory", onChatHistory);
    socket.on("receive_message", onReceiveMessage);
    socket.on("agentJoined", onAgentJoined);
    socket.on("typing", onTyping);
    socket.on("stop_typing", onStopTyping);

    // Connect or fire immediately if already connected
    if (socket.connected) {
      onConnect();
    } else {
      socket.connect();
    }

    // Cleanup: remove ONLY our named listeners, not all listeners
    return () => {
      socket.off("connect", onConnect);
      socket.off("chatHistory", onChatHistory);
      socket.off("receive_message", onReceiveMessage);
      socket.off("agentJoined", onAgentJoined);
      socket.off("typing", onTyping);
      socket.off("stop_typing", onStopTyping);
    };
  }, [roomId, user.userId, ticket?.ticketId]);

  // Auto scroll
  useEffect(() => {
    chatContainerRef.current?.scrollTo({
      top: chatContainerRef.current.scrollHeight,
      behavior: "smooth",
    });
  }, [messages, typing]);

  // ── Typing indicator emit ─────────────────────────────────
  const handleInputChange = (e) => {
    setMessageInput(e.target.value);

    // Emit typing start only once until they stop
    if (!isTypingRef.current) {
      isTypingRef.current = true;
      socket.emit("typing", roomId);
    }

    // Reset stop-typing timer on every keystroke
    clearTimeout(typingTimerRef.current);
    typingTimerRef.current = setTimeout(() => {
      isTypingRef.current = false;
      socket.emit("stop_typing", roomId);
    }, 1500);
  };

  // ── Send text ─────────────────────────────────────────────
  const sendMessage = () => {
    if (!messageInput.trim()) return;

    // Clear typing state immediately on send
    clearTimeout(typingTimerRef.current);
    isTypingRef.current = false;
    socket.emit("stop_typing", roomId);

    const tempId = `temp-${++messageIdRef.current}`;

    setMessages(prev => [...prev, {
      _id: tempId,
      bubbleSide: "user",
      msgType: "text",
      text: messageInput,
      timestamp: Date.now(),
      status: "sending",
    }]);
    setMessageInput("");

    socket.emit("send_message", {
      ticketId: ticket?.ticketId,
      roomId,
      senderId: user.userId,
      senderType: "customer",
      type: "text",
      text: messageInput,
      tempId,
    });
  };

  // ── File upload ───────────────────────────────────────────
  const uploadFile = async (file, msgType, duration) => {
    const tempId = `temp-${++messageIdRef.current}`;

    setMessages(prev => [...prev, {
      _id: tempId,
      bubbleSide: "user",
      msgType,
      text: msgType === "audio" ? "Voice Message" : file.name,
      timestamp: Date.now(),
      status: "sending",
      duration: duration || undefined,
    }]);

    const fd = new FormData();
    fd.append("file", file);

    try {
      const res = await axios.post("http://localhost:30032/api/mtrade/upload", fd);
      socket.emit("send_message", {
        ticketId: ticket?.ticketId,
        roomId,
        senderId: user.userId,
        senderType: "customer",
        type: msgType,
        fileUrl: res.data.url,
        text: msgType === "audio" ? "Voice Message" : file.name,
        tempId,
        duration: duration || undefined,
      });
    } catch (err) {
      console.error(err);
      setMessages(prev => prev.filter(m => m._id !== tempId));
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    let msgType = "document";
    if (file.type.startsWith("image/")) msgType = "image";
    if (file.type.startsWith("audio/")) msgType = "audio";
    uploadFile(file, msgType);
    e.target.value = ""; // reset so same file can be re-selected
  };

  // ── Voice recorder ────────────────────────────────────────
  const toggleRecording = async () => {
    if (isRecording) {
      mediaRecorderRef.current?.stop();
      clearInterval(timerRef.current);
      setIsRecording(false);
    } else {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        mediaRecorderRef.current = new MediaRecorder(stream, {
          mimeType: "audio/webm;codecs=opus",
        });
        audioChunksRef.current = [];

        mediaRecorderRef.current.ondataavailable = (e) =>
          audioChunksRef.current.push(e.data);

        mediaRecorderRef.current.onstop = () => {
          const blob = new Blob(audioChunksRef.current, { type: "audio/webm" });
          setRecordingBlob(blob);
          setAudioPreviewUrl(URL.createObjectURL(blob));
          stream.getTracks().forEach(t => t.stop());
        };

        mediaRecorderRef.current.start();
        setIsRecording(true);
        setRecordingDuration(0);
        timerRef.current = setInterval(
          () => setRecordingDuration(p => p + 1),
          1000
        );
      } catch {
        alert("Microphone access denied.");
      }
    }
  };

  const sendVoiceMessage = () => {
    if (!recordingBlob) return;
    uploadFile(recordingBlob, "audio", recordingDuration);
    setAudioPreviewUrl(null);
    setRecordingBlob(null);
    setRecordingDuration(0);
  };

  const cancelRecording = () => {
    if (mediaRecorderRef.current && isRecording) mediaRecorderRef.current.stop();
    clearInterval(timerRef.current);
    setIsRecording(false);
    setAudioPreviewUrl(null);
    setRecordingBlob(null);
    setRecordingDuration(0);
  };

  // ── Render message ────────────────────────────────────────
  const renderMessage = (msg) => {
    const isUser = msg.bubbleSide === "user";
    const isSystem = msg.bubbleSide === "system";

    // Use createdAt (from DB) or timestamp (optimistic)
    const rawTime = msg.createdAt || msg.timestamp;
    const time = rawTime ? new Date(rawTime) : new Date();
    const timeString = isNaN(time.getTime())
      ? "just now"
      : time.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

    if (isSystem) {
      return (
        <div key={msg._id} className="flex justify-center my-1">
          <span className="bg-slate-200 text-slate-500 text-xs px-4 py-1.5 rounded-full">
            {msg.text}
          </span>
        </div>
      );
    }

    return (
      <div key={msg._id} className={`flex ${isUser ? "justify-end" : "justify-start"}`}>
        {/* Agent avatar */}
        {!isUser && (
          <div className="w-7 h-7 rounded-full bg-emerald-100 flex items-center justify-center text-xs font-bold text-emerald-700 mr-2 mt-auto mb-1 flex-shrink-0">
            A
          </div>
        )}
        <div className={`max-w-[75%] px-4 py-3 rounded-2xl text-sm shadow-sm ${
          isUser
            ? "bg-emerald-600 text-white rounded-br-sm"
            : "bg-white border border-slate-100 text-slate-800 rounded-bl-sm"
        }`}>
          {msg.msgType === "image" && (
            <img src={msg.fileUrl} alt="" className="rounded-xl max-w-full mb-1" />
          )}
          {msg.msgType === "audio" && (
            <div className="min-w-[200px]">
              <audio controls className="w-full h-8">
                <source src={msg.fileUrl} type="audio/webm" />
              </audio>
              {msg.duration && (
                <p className="text-xs opacity-70 mt-1 text-right">{msg.duration}s</p>
              )}
            </div>
          )}
          {msg.msgType === "document" && (
            <a
              href={msg.fileUrl}
              target="_blank"
              rel="noreferrer"
              className="flex items-center gap-2 underline underline-offset-2"
            >
              📄 {msg.text}
            </a>
          )}
          {msg.msgType === "text" && (
            <p className="whitespace-pre-wrap leading-relaxed">{msg.text}</p>
          )}
          <div className={`text-[10px] mt-1.5 flex items-center gap-1 ${
            isUser ? "justify-end opacity-75" : "justify-end text-slate-400"
          }`}>
            {timeString}
            {isUser && (
              <span>
                {msg.status === "sending" ? (
                  <span className="opacity-60">⋯</span>
                ) : (
                  <span className="text-emerald-200">✓✓</span>
                )}
              </span>
            )}
          </div>
        </div>
      </div>
    );
  };

  // ── Render ────────────────────────────────────────────────
  return (
    <div className="flex flex-col flex-1 bg-white rounded-2xl shadow-lg overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white px-5 py-4 flex items-center gap-3">
        <div className="w-9 h-9 bg-white/20 rounded-full flex items-center justify-center text-lg">
          🎧
        </div>
        <div>
          <p className="font-semibold text-sm">Customer Support</p>
          <p className="text-emerald-100 text-xs">
            {stage === "chat" ? "Agent connected" : "Finding an agent…"}
          </p>
        </div>
        {stage === "chat" && (
          <div className="ml-auto flex items-center gap-1.5 bg-white/15 px-3 py-1 rounded-full">
            <div className="w-2 h-2 bg-green-300 rounded-full animate-pulse" />
            <span className="text-xs font-medium">Live</span>
          </div>
        )}
      </div>

      {/* Waiting state */}
      {stage === "waiting" && (
        <div className="flex-1 flex flex-col items-center justify-center p-8 text-center bg-slate-50">
          <div className="relative mb-6">
            <div className="w-20 h-20 rounded-full bg-amber-100 flex items-center justify-center text-4xl">
              ⏳
            </div>
            <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-amber-400 rounded-full animate-pulse" />
          </div>
          <h3 className="text-lg font-bold text-slate-800 mb-2">Looking for an agent</h3>
          <p className="text-slate-500 text-sm mb-5 max-w-xs">
            Your enquiry is in the queue. We'll connect you as soon as an agent is available.
          </p>
          <div className="bg-white border border-slate-200 rounded-2xl px-5 py-3 text-sm">
            <span className="text-slate-400">Ticket </span>
            <span className="font-mono font-semibold text-slate-700">{ticket?.ticketId}</span>
          </div>
        </div>
      )}

      {/* Chat state */}
      {stage === "chat" && (
        <>
          {/* Messages */}
          <div
            ref={chatContainerRef}
            className="flex-1 bg-slate-50 p-4 overflow-y-auto space-y-3"
          >
            {messages.length === 0 && (
              <div className="flex justify-center pt-8">
                <p className="text-slate-400 text-sm">Start of conversation</p>
              </div>
            )}
            {messages.map(renderMessage)}

            {/* Typing bubble */}
            {typing && (
              <div className="flex items-end gap-2">
                <div className="w-7 h-7 rounded-full bg-emerald-100 flex items-center justify-center text-xs font-bold text-emerald-700 flex-shrink-0">
                  A
                </div>
                <div className="bg-white border border-slate-100 px-4 py-3 rounded-2xl rounded-bl-sm shadow-sm flex gap-1 items-center">
                  <span className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                  <span className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                  <span className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
                </div>
              </div>
            )}
          </div>

          {/* Input area */}
          <div className="bg-white border-t border-slate-100 p-3">
            {/* Voice preview */}
            {audioPreviewUrl && (
              <div className="mb-3 p-3 bg-slate-50 border border-slate-200 rounded-2xl">
                <audio controls src={audioPreviewUrl} className="w-full mb-2 h-8" />
                <div className="flex gap-2">
                  <button
                    onClick={sendVoiceMessage}
                    className="flex-1 bg-emerald-600 text-white py-2.5 rounded-xl font-medium text-sm"
                  >
                    Send ({recordingDuration}s)
                  </button>
                  <button
                    onClick={cancelRecording}
                    className="flex-1 bg-slate-200 text-slate-600 py-2.5 rounded-xl font-medium text-sm"
                  >
                    Discard
                  </button>
                </div>
              </div>
            )}

            <div className={`flex items-center gap-1.5 rounded-2xl px-2 py-1 border transition-colors ${
              isRecording
                ? "bg-red-50 border-red-200"
                : "bg-slate-50 border-slate-200 focus-within:border-emerald-400"
            }`}>
              {/* Attach */}
              <button
                onClick={() => fileInputRef.current.click()}
                className="w-9 h-9 hover:bg-slate-200 rounded-xl flex items-center justify-center text-slate-500 transition-colors"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                </svg>
              </button>
              <input
                type="file"
                ref={fileInputRef}
                className="hidden"
                onChange={handleFileSelect}
              />

              {/* Mic */}
              <button
                onClick={toggleRecording}
                className={`w-9 h-9 rounded-xl flex items-center justify-center transition-all ${
                  isRecording
                    ? "bg-red-500 text-white animate-pulse"
                    : "hover:bg-slate-200 text-slate-500"
                }`}
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/>
                  <path d="M19 10v2a7 7 0 0 1-14 0v-2H3v2a9 9 0 0 0 8 8.94V23h2v-2.06A9 9 0 0 0 21 12v-2h-2z"/>
                </svg>
              </button>

              {/* Text input */}
              <input
                type="text"
                value={messageInput}
                onChange={handleInputChange}
                onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && sendMessage()}
                className="flex-1 bg-transparent outline-none px-2 py-2.5 text-sm text-slate-800 placeholder-slate-400"
                placeholder={isRecording ? `Recording… ${recordingDuration}s` : "Type a message…"}
                disabled={isRecording}
              />

              {/* Send */}
              <button
                onClick={sendMessage}
                disabled={!messageInput.trim() || isRecording}
                className="w-9 h-9 bg-emerald-600 disabled:bg-slate-200 disabled:text-slate-400 text-white rounded-xl flex items-center justify-center transition-colors"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5}
                    d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                </svg>
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default SupportChat;
